import React from 'react';
import { Anchor, Checkbox, Heading, Label, LabeledControl, Modal, Toast, useToast } from '@zillow/constellation-10';
import type { UseMutateAsyncFunction } from '@zg-rentals/react-query5';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useIdVerificationWebApiHandler_getTermSet } from '@zg-rentals/ts-id-verification-client-react-query';
import { TermSetId, type TermType, type WebApiTerm } from '@zg-rentals/ts-id-verification-client';
import TermsHtml from './TermsHtml';
import type { TrackEvent } from '../../../clickstream/events';
import { trackTermAcceptance, trackTermView } from './TermsCheckbox.utils';
import { TermSet } from '../../../../../models';
import type { IdVerificationWebApiHandler_acceptTerms_Args } from '@zg-rentals/ts-id-verification-client-react-query';
import { useIdVerificationCallbacks, useTermSet } from '../../../IdvContext';

function generateTermsTitleElements(
  terms: Array<WebApiTerm>,
  trackTermView: (termSet: TermSet, termType: TermType, trackEvent: TrackEvent) => void,
  termSet: TermSet,
  trackEvent: TrackEvent,
) {
  return terms.map((term, index) => {
    const isLast = index === terms.length - 1;
    const isSecondToLast = index === terms.length - 2;
    const getConjunction = () => {
      if (isLast) {
        return '';
      }
      if (isSecondToLast) {
        return terms.length === 2 ? ' and ' : ', and ';
      }
      return ', ';
    };

    return (
      <React.Fragment key={term.id as string}>
        <Modal
          size={{ base: 'fullScreen', md: 'lg' }}
          trigger={
            <Anchor
              asChild
              onClick={() => {
                trackTermView(termSet, term.id, trackEvent);
              }}
            >
              <button type="button">{term.title}</button>
            </Anchor>
          }
          header={
            <Heading level={2} textStyle="heading-sm">
              {term.title}
            </Heading>
          }
          body={<TermsHtml termType={term.id} currentVersion={term.currentVersion} />}
        />
        {getConjunction()}
      </React.Fragment>
    );
  });
}

function generateTermsLabelJSX(
  terms: Array<WebApiTerm>,
  trackTermView: (termSet: TermSet, termType: TermType, trackEvent: TrackEvent) => void,
  termSet: TermSet,
  trackEvent: TrackEvent,
): React.ReactNode {
  const textOnlyTerms = terms.filter((term) => term.text && !term.title);
  const titleOnlyTerms = terms.filter((term) => term.title && !term.text);

  // Case 1: Only text terms
  if (textOnlyTerms.length === terms.length) {
    const textContent = textOnlyTerms.map((term) => term.text).join('; ');
    return <>{textContent}</>;
  }
  // Case 2: Only title terms
  if (titleOnlyTerms.length === terms.length) {
    const titleElements = generateTermsTitleElements(titleOnlyTerms, trackTermView, termSet, trackEvent);
    return <>Read and accept {titleElements}</>;
  }
  // Case 3: Mixed terms (both text and title)
  const titleElements = generateTermsTitleElements(titleOnlyTerms, trackTermView, termSet, trackEvent);
  const textContent = textOnlyTerms.map((term) => term.text).join('; ');
  return (
    <>
      {titleOnlyTerms.length > 0 && (
        <>
          Read and accept {titleElements}
          {textOnlyTerms.length > 0 && '; '}
        </>
      )}
      {textOnlyTerms.length > 0 && <>{textContent}</>}
    </>
  );
}

export function termSetToTermSetId(termSet: TermSet): TermSetId {
  switch (termSet) {
    case TermSet.LandlordScreening:
      return TermSetId.LANDLORD_SCREENING;
    case TermSet.RenterScreening:
      return TermSetId.RENTER_SCREENING;
    default:
      throw new Error(`Unknown TermSet: ${termSet}`);
  }
}

interface TermsCheckboxProps {
  terms: Array<WebApiTerm>;
  markAcceptedTerms: UseMutateAsyncFunction<void, unknown, IdVerificationWebApiHandler_acceptTerms_Args, unknown>;
  [key: string]: unknown;
}

interface AcceptDeclineTermParams {
  termTypes: Array<TermType>;
  onClose?: () => void;
}

export default function TermsCheckbox({ terms, markAcceptedTerms, ...props }: TermsCheckboxProps) {
  const { enqueueToast } = useToast();
  const termSet = useTermSet();
  const termSetId = termSetToTermSetId(termSet);
  const allTermsAccepted = terms.every((term) => term.isCurrent);
  const allTermTypes = terms.map((term) => term.id);
  const queryClient = useQueryClient();
  const getTermSetKey = useIdVerificationWebApiHandler_getTermSet.getKey({ termSetId });
  const { trackEvent } = useIdVerificationCallbacks();

  const handleTermsError = (action: string, termCount: number) => {
    enqueueToast(
      <Toast tone="critical">
        {`Unable to ${action} ${termCount} term${termCount === 1 ? '' : 's'}. Try again later.`}
      </Toast>,
    );
    queryClient.invalidateQueries({ queryKey: getTermSetKey });
  };

  function onAccept({ termTypes }: AcceptDeclineTermParams) {
    const termsToAccept = termTypes.map((termType) => {
      const termToAccept = terms.find((t) => t.id === termType);
      return {
        termType,
        version: termToAccept?.currentVersion ?? 0,
      };
    });

    markAcceptedTerms({ postData: { termsToAccept } })
      .then(() => {
        termTypes.forEach((termType) => {
          trackTermAcceptance(termSet, termType, trackEvent);
        });
        queryClient.invalidateQueries({ queryKey: getTermSetKey });
        return undefined;
      })
      .catch(() => handleTermsError('accept', termTypes.length));
  }

  function onDecline({ termTypes }: AcceptDeclineTermParams) {
    const termsToDecline = termTypes.map((termType) => ({
      termType,
      version: -1,
    }));

    markAcceptedTerms({ postData: { termsToAccept: termsToDecline } })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: getTermSetKey });
        return undefined;
      })
      .catch(() => handleTermsError('decline', termTypes.length));
  }

  const labelContent = generateTermsLabelJSX(terms, trackTermView, termSet, trackEvent);
  return (
    <LabeledControl
      control={
        <Checkbox
          checked={allTermsAccepted}
          onClick={() =>
            allTermsAccepted ? onDecline({ termTypes: allTermTypes }) : onAccept({ termTypes: allTermTypes })
          }
        />
      }
      label={<Label>{labelContent}</Label>}
      {...props}
    />
  );
}
